/**
 * Eric BYOK (2026-05-11) — Page Settings → Forfait AI.
 *
 * Chaque organisation configure ses propres clés API pour OpenAI, AssemblyAI et
 * son stockage canadien (AWS S3 ca-central-1). Les clés sont chiffrées AES256
 * côté serveur. Aucune clé n'est jamais retournée en clair côté frontend (mask : prefix + last 4).
 *
 * Knoria n'agit pas comme revendeur — chaque compagnie paie directement OpenAI,
 * AssemblyAI et AWS. C'est le modèle "Forfait AI" exigé par Eric.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "../components/ui/sonner";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";
import { apiErrorMessage } from "../lib/apiError";
import AIReportSettingsCard from "../components/admin/AIReportSettingsCard";
import ConsumptionSeriesChart from "../components/admin/ConsumptionSeriesChart";
// Tâche 1151 — Le domaine d'envoi Resend se configure ici : le diagnostic doit
// être visible au même endroit que l'adresse expéditrice.
import EmailDomainGuide from "../components/admin/EmailDomainGuide";
import MasterKeyCard from "../components/byok/MasterKeyCard"; // Tâche 1206
import AssemblyAiDiagnosticButton from "../components/byok/AssemblyAiDiagnosticButton"; // Tâche 1301
// Tâche 1152 — Quota d'envoi Resend : le mur arrive sans préavis.
import ResendQuotaPanel from "../components/admin/ResendQuotaPanel";
// Tâche 1156 — Envoi test vers sa propre adresse, en un clic.
import SelfTestEmailButton from "../components/admin/SelfTestEmailButton";

const SERVICE_LOGOS = {
  openai: { icon: "🤖", color: "emerald" },
  assemblyai: { icon: "🎙️", color: "blue" },
  ovh_storage: { icon: "🍁", color: "rose" },
  resend: { icon: "📧", color: "amber" },
  // Tâche 1494 — Les vecteurs de recherche : sans entrée ici, la carte
  // s'affichait avec la prise générique, comme un service non reconnu.
  embeddings: { icon: "🧭", color: "violet" },
};

// Tâche 1420 (Eric 2026-09-30) — Le catalogue des services est RÉDIGÉ par le
// serveur (core/byok). Il envoie les deux langues ; un service d'avant cette
// tâche (sans champ anglais) garde son texte français plutôt que de se vider.
const champLangue = (objet, cle, lang) =>
  (lang === "en" ? objet?.[`${cle}_en`] : null) || objet?.[cle] || "";
const libelleService = (svc, lang) => champLangue(svc, "label", lang);
const descriptionService = (svc, lang) => champLangue(svc, "description", lang);
const coutService = (svc, lang) => champLangue(svc, "estimated_monthly_cost_cad", lang);

export default function AIPackagePage() {
  const { lang, tr } = useLang();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service being edited
  const [formData, setFormData] = useState({ key: "", bucket_name: "", region: "bhs", endpoint: "https://s3.bhs.io.cloud.ovh.net", secret_key: "" });
  const [saving, setSaving] = useState(false);

  // Eric 2026-05-12 — Budget mensuel + alertes quota
  const [budget, setBudget] = useState({ monthly_cad: "", alerts_enabled: true, configured: false });
  const [savingBudget, setSavingBudget] = useState(false);

  // Vincent #148 (2026-06-12) — Test de connexion réel par service.
  // testResults[service] = {ok, message, suggestion, key_last4, source, ...}
  const [testResults, setTestResults] = useState({});
  const [testingService, setTestingService] = useState(null);
  const runConnectionTest = async (service) => {
    if (testingService) return;
    setTestingService(service);
    setTestResults((p) => ({ ...p, [service]: null }));
    try {
      const { data } = await axios.post(`${API}/api/org/ai-package/${service}/test`, {}, { withCredentials: true, timeout: 45000 });
      setTestResults((p) => ({ ...p, [service]: data }));
      if (data.ok) toast.success(lang === "fr" ? "Test réussi — clé opérationnelle" : "Test passed");
      else toast.error(lang === "fr" ? "Le test a échoué — voir le diagnostic" : "Test failed — see diagnostic");
    } catch (e) {
      setTestResults((p) => ({ ...p, [service]: { ok: false, message: e.response?.data?.detail || e.message } }));
      toast.error(e.response?.data?.detail || tr("Échec du test de connexion", "Connection test failed"));
    } finally {
      setTestingService(null);
    }
  };

  // Vincent #149 (2026-06-12) — Consommation : mois en cours + moyennes 3/12 mois.
  const [consumptionSummary, setConsumptionSummary] = useState(null);

  // Eric (2026-06-15 — iter102) — Health Card : test simultané des 3 services.
  const [testingAll, setTestingAll] = useState(false);
  const runTestAll = async () => {
    if (testingAll) return;
    setTestingAll(true);
    try {
      const { data } = await axios.post(`${API}/api/org/ai-package/test-all`, {}, { withCredentials: true, timeout: 90000 });
      // Mettre à jour chaque résultat individuel
      Object.entries(data.results || {}).forEach(([svc, res]) => {
        setTestResults((p) => ({ ...p, [svc]: res }));
      });
      if (data.all_healthy) {
        toast.success(lang === "fr" ? `✅ Tous les services sont opérationnels (${data.healthy_count}/${data.total_count})` : `All services healthy (${data.healthy_count}/${data.total_count})`);
      } else {
        toast.warning(lang === "fr" ? `⚠️ ${data.healthy_count}/${data.total_count} services opérationnels — voir détails ci-dessous` : `${data.healthy_count}/${data.total_count} services healthy`);
      }
      // Recharger pour mettre à jour last_test
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec du test global" : "Test all failed"));
    } finally {
      setTestingAll(false);
    }
  };

  // B1097 (Eric 2026-09-06) — Commutateur par clé : mettre une clé en pause
  // sans la supprimer (donc sans avoir à la ressaisir). Une clé en pause n'est
  // plus jamais lue côté serveur : le service devient indisponible pour cette
  // organisation, sauf si « forcer les clés Knoria » est activé.
  const [togglingService, setTogglingService] = useState(null);

  const toggleKeyEnabled = async (service, enabled) => {
    setTogglingService(service);
    try {
      await axios.put(
        `${API}/api/org/ai-package/keys/${service}/enabled`,
        { enabled },
        { withCredentials: true },
      );
      toast.success(
        enabled
          ? (lang === "fr" ? "Clé réactivée" : "Key re-enabled")
          : (lang === "fr" ? "Clé mise en pause — le service est indisponible pour cette organisation" : "Key paused"),
      );
      await loadStatus();
    } catch (e) {
      toast.error(apiErrorMessage(e, lang === "fr" ? "Changement impossible" : "Toggle failed"));
    } finally {
      setTogglingService(null);
    }
  };

  const loadStatus = async () => {
    try {
      const [statusResp, budgetResp] = await Promise.all([
        axios.get(`${API}/api/org/ai-package/status`, { withCredentials: true }),
        axios.get(`${API}/api/org/ai-package/budget`, { withCredentials: true }),
      ]);
      setStatus(statusResp.data);
      setBudget({
        monthly_cad: budgetResp.data.monthly_cad ?? "",
        alerts_enabled: budgetResp.data.alerts_enabled ?? true,
        configured: budgetResp.data.configured,
      });
      // Vincent #149 — résumé conso (non bloquant)
      axios.get(`${API}/api/org/ai-package/consumption-summary`, { withCredentials: true })
        .then((r) => setConsumptionSummary(r.data))
        .catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Impossible de charger le Forfait AI", "Could not load the AI Package"));
    } finally {
      setLoading(false);
    }
  };

  const saveBudget = async () => {
    if (savingBudget) return;
    setSavingBudget(true);
    try {
      const monthly = budget.monthly_cad === "" || budget.monthly_cad === null ? null : Number(budget.monthly_cad);
      if (monthly !== null && (isNaN(monthly) || monthly < 0)) {
        toast.error(lang === "fr" ? "Budget invalide" : "Invalid budget");
        setSavingBudget(false);
        return;
      }
      await axios.post(`${API}/api/org/ai-package/budget`, { monthly_cad: monthly, alerts_enabled: budget.alerts_enabled }, { withCredentials: true });
      toast.success(lang === "fr" ? "Budget sauvegardé" : "Budget saved");
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec de la sauvegarde" : "Save failed"));
    } finally {
      setSavingBudget(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const openEditor = (service) => {
    setEditing(service);
    setFormData({ key: "", bucket_name: "", region: "bhs", endpoint: "https://s3.bhs.io.cloud.ovh.net", secret_key: "" });
  };

  const saveKey = async (service) => {
    if (saving) return;
    if (!formData.key || formData.key.length < 8) {
      toast.error(tr("Clé invalide (minimum 8 caractères)", "Invalid key (8 characters minimum)"));
      return;
    }
    setSaving(true);
    try {
      const payload = { key: formData.key.trim() };
      if (service === "ovh_storage") {
        if (!formData.bucket_name) { toast.error(tr("Le nom du bucket OVH est requis", "The OVH bucket name is required")); setSaving(false); return; }
        if (!formData.secret_key) { toast.error(tr("La OVH Secret Access Key est requise", "The OVH Secret Access Key is required")); setSaving(false); return; }
        payload.bucket_name = formData.bucket_name.trim();
        payload.region = formData.region || "bhs";
        payload.endpoint = formData.endpoint || "https://s3.bhs.io.cloud.ovh.net";
        payload.secret_key = formData.secret_key.trim();
      }
      if (service === "resend") {
        if (!formData.sender_email) { toast.error(tr("Le sender_email est requis (ex: noreply@votre-domaine.com)", "sender_email is required (e.g. noreply@your-domain.com)")); setSaving(false); return; }
        payload.sender_email = formData.sender_email.trim();
        if (formData.reply_to) payload.reply_to = formData.reply_to.trim();
      }
      const resp = await axios.post(`${API}/api/org/ai-package/${service}`, payload, { withCredentials: true });
      toast.success(lang === "fr" ? `Clé ${service} validée et sauvegardée` : `${service} key validated and saved`);
      // Vincent 2026-02-06 — Si le backend a remonté un warning non-bloquant
      // (ex: domaine sender Resend non vérifié), on l'affiche en plus du succès.
      if (resp?.data?.warning) {
        toast.warning(resp.data.warning, { duration: 12000 });
      }
      setEditing(null);
      loadStatus();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail), { duration: 10000 });
    } finally {
      setSaving(false);
    }
  };

  // Eric BYOK-AUDIT (2026-02-06) — État de la modale de confirmation typée pour DELETE.
  // Remplace l'ancien window.confirm() pour empêcher les suppressions accidentelles
  // (cause probable de la disparition des clés Knoria du 2026-02-06).
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { service, name, masked, typed }

  const deleteKey = (service) => {
    const meta = (status?.services || []).find(s => s.service === service);
    setDeleteConfirm({
      service,
      name: meta?.name || service,
      masked: meta?.key_masked || "••••",
      typed: "",
    });
  };

  const confirmDeleteKey = async () => {
    if (!deleteConfirm || deleteConfirm.typed.trim().toUpperCase() !== "SUPPRIMER") return;
    const { service } = deleteConfirm;
    try {
      await axios.delete(`${API}/api/org/ai-package/${service}`, { withCredentials: true });
      toast.success(lang === "fr" ? "Clé supprimée — l'action a été enregistrée dans le journal d'audit." : "Key deleted — action logged to audit trail.");
      setDeleteConfirm(null);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Échec de la suppression", "Deletion failed"));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-zinc-500 mt-4">Chargement...</p>
      </div>
    );
  }

  const services = status?.services || [];
  const isComplete = status?.complete;

  return (
    <div className="p-4 lg:p-6 max-w-4xl" data-testid="ai-package-page">
      <div className="mb-6 mt-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-zinc-900">📦 {lang === "fr" ? "Intégrations & Services (Forfait AI)" : "Integrations & Services (AI Package)"}</h1>
            <p className="text-sm text-zinc-600 mt-2">
              {lang === "fr"
                ? "Knoria utilise vos propres clés API. Vous payez directement OpenAI, AssemblyAI et votre fournisseur de stockage canadien — Knoria ne prélève rien sur votre consommation IA."
                : "Knoria uses your own API keys. You pay OpenAI, AssemblyAI and your Canadian storage provider directly — Knoria takes no cut on your AI usage."}
            </p>
          </div>
          <Link
            to="/dashboard/settings/ai-package/guide"
            className="px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-medium whitespace-nowrap"
            data-testid="ai-package-guide-link"
          >
            📘 {lang === "fr" ? "Guide pas-à-pas" : "Step-by-step guide"}
          </Link>
          <Link
            to="/dashboard/settings/ai-package/consumption"
            className="px-3 py-2 rounded-lg border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 text-xs font-medium whitespace-nowrap"
            data-testid="ai-package-consumption-link"
          >
            📊 {lang === "fr" ? "Voir ma consommation" : "View consumption"}
          </Link>
          {/* Vincent #148 (Eric 2026-02-06) — Rapport téléchargeable de l'état Forfait AI */}
          <button
            onClick={async () => {
              try {
                const r = await axios.get(`${API}/api/org/ai-package/status-report`, { withCredentials: true, responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/plain;charset=utf-8" }));
                const a = document.createElement("a");
                a.href = url;
                // Récupère le filename depuis le header Content-Disposition
                const cd = r.headers["content-disposition"] || "";
                const m = cd.match(/filename="?([^"]+)"?/);
                a.download = m ? m[1] : `forfait-ai-${Date.now()}.txt`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success(lang === "fr" ? "Rapport téléchargé" : "Report downloaded");
              } catch (e) {
                toast.error(e.response?.data?.detail || tr("Erreur de génération du rapport", "Report generation failed"));
              }
            }}
            className="px-3 py-2 rounded-lg border border-violet-300 bg-white hover:bg-violet-50 text-violet-700 text-xs font-medium whitespace-nowrap inline-flex items-center gap-1"
            data-testid="ai-package-download-report-btn"
            title={lang === "fr" ? "Télécharge un rapport texte avec l'état des connexions, le budget et la consommation" : "Download a text report with connections state, budget and usage"}
          >
            📄 {lang === "fr" ? "Rapport téléchargeable" : "Download report"}
          </button>
          {/* Vincent #148 (Eric 2026-05-16) — Lien vers l'audit BYOK */}
          <Link
            to="/dashboard/settings/ai-package/audit"
            className="px-3 py-2 rounded-lg border border-teal-300 bg-white hover:bg-teal-50 text-teal-700 text-xs font-medium whitespace-nowrap"
            data-testid="ai-package-audit-link"
          >
            🔒 {lang === "fr" ? "Audit BYOK" : "BYOK Audit"}
          </Link>
        </div>
        {isComplete ? (
          <div className="mt-4 px-4 py-3 rounded-lg bg-teal-50 border border-teal-200 text-teal-800 text-sm" data-testid="ai-package-complete-banner">
            ✅ {lang === "fr" ? "Forfait AI complet — toutes les fonctionnalités sont activées." : "AI Package complete — all features enabled."}
          </div>
        ) : (
          <div className="mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm space-y-2" data-testid="ai-package-incomplete-banner">
            <div className="flex items-start gap-2">
              <span className="text-base">⚠️</span>
              <div className="flex-1">
                <div className="font-semibold mb-1">
                  {lang === "fr"
                    ? `Forfait AI incomplet (${status?.configured_count}/${status?.total_count} services configurés)`
                    : `AI Package incomplete (${status?.configured_count}/${status?.total_count} services configured)`}
                </div>
                <div className="text-amber-800">
                  {lang === "fr"
                    ? "Pour activer toutes les fonctionnalités IA (chatbot, génération Q&R, scripts, procédures, transcription, stockage Canada, envoi des courriels aux utilisateurs invités et ajoutés), vous devez configurer vos propres clés API ci-dessous. Knoria ne facture aucun supplément — vous payez directement OpenAI, AssemblyAI, OVH et Resend."
                    : "To enable all AI features (chatbot, Q&A generation, scripts, procedures, transcription, Canada storage, transactional emails to invited and added users), you must configure your own API keys below. Knoria charges no markup — you pay OpenAI, AssemblyAI, OVH and Resend directly."}
                </div>
                <div className="mt-2 text-xs text-amber-700">
                  {lang === "fr"
                    ? "👉 Cliquez sur « Configurer » à droite de chaque service ci-dessous pour entrer votre clé API. Le bouton « 📘 Guide pas-à-pas » en haut détaille la marche à suivre pour créer chaque compte si vous ne les avez pas encore."
                    : "👉 Click \"Configuré\" next to each service below to enter your API key. The \"📘 Step-by-step guide\" button at the top explains how to create each account."}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tâche 1206 (Eric 2026-06-26) — Clé maîtresse de chiffrement. La carte
          ne s'affiche qu'au fondateur, et seulement s'il y a quelque chose à
          faire : elle permet d'auditer et de migrer sans terminal ni MongoDB. */}
      <MasterKeyCard />

      <EmailDomainGuide />
      <ResendQuotaPanel />
      <div className="mb-6">
        <SelfTestEmailButton />
      </div>

      {/* Eric (2026-06-15 — iter102) — Health Card : carte synthétique avec
          un badge couleur par service + bouton "Tester tous". Permet aux
          admins d'avoir une vue d'état immédiate sans devoir cliquer 4 fois. */}
      <div className="mb-6 border border-zinc-200 rounded-xl p-5 bg-gradient-to-br from-white to-zinc-50" data-testid="ai-package-health-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
              🩺 {lang === "fr" ? "Santé du Forfait AI" : "AI Package Health"}
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              {lang === "fr"
                ? "État en temps réel de vos connexions AI. Le test fait un mini-ping authentifié sur chaque service."
                : "Real-time state of your AI connections. The test runs an authenticated mini-ping on each service."}
            </p>
            {/* Tâche 1207 (Eric 2026-06-26) — Les pastilles ci-dessous étaient
                mises à jour UNIQUEMENT quand quelqu'un cliquait « Tester ». La
                Sentinelle Clés IA les rafraîchit maintenant toutes les 30 min
                et alerte par courriel dès qu'une clé est refusée : on le dit,
                sinon « testé il y a 3 j » laisse croire que rien ne surveille. */}
            <p className="text-xs text-teal-700 mt-1" data-testid="ai-package-health-sentinel-note">
              {lang === "fr"
                ? "Surveillance automatique : la Sentinelle Clés IA teste ces services toutes les 30 minutes et alerte par courriel dès qu'une clé est refusée."
                : "Automatic monitoring: the AI Keys Sentinel tests these services every 30 minutes and emails an alert as soon as a key is refused."}
            </p>
          </div>
          <button
            onClick={runTestAll}
            disabled={testingAll}
            className="px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#001a6b] disabled:bg-zinc-300 text-white text-xs font-semibold whitespace-nowrap inline-flex items-center gap-2 transition-colors"
            data-testid="ai-package-health-test-all-btn"
          >
            {testingAll
              ? (lang === "fr" ? "⏳ Test en cours…" : "⏳ Testing…")
              : (lang === "fr" ? "🧪 Tester tous mes services" : "🧪 Test all services")}
          </button>
        </div>
        <AssemblyAiDiagnosticButton lang={lang} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-package-health-badges">
          {services.map((svc) => {
            const meta = SERVICE_LOGOS[svc.service] || { icon: "🔌", color: "zinc" };
            const lt = svc.last_test;
            const configured = svc.configured;
            // 3 états visuels :
            //   - non configuré : neutre (gris)
            //   - configuré + last_test.ok : vert
            //   - configuré + last_test.ok=false : rouge
            //   - configuré + pas de last_test : jaune (jamais testé)
            const state = !configured
              ? "unconfigured"
              : lt == null
                ? "untested"
                : lt.ok ? "healthy" : "failing";
            const stateColors = {
              unconfigured: { ring: "border-zinc-200", dot: "bg-zinc-400", label: lang === "fr" ? "Non configuré" : "Not set", textCls: "text-zinc-500" },
              untested: { ring: "border-amber-300", dot: "bg-amber-400", label: lang === "fr" ? "Jamais testé" : "Never tested", textCls: "text-amber-700" },
              healthy: { ring: "border-teal-300 bg-teal-50/40", dot: "bg-teal-500", label: lang === "fr" ? "Opérationnel" : "Healthy", textCls: "text-teal-700" },
              failing: { ring: "border-rose-300 bg-rose-50/40", dot: "bg-rose-500", label: lang === "fr" ? "En erreur" : "Failing", textCls: "text-rose-700" },
            }[state];
            const testedAgo = lt?.tested_at
              ? (() => {
                  const diff = (Date.now() - new Date(lt.tested_at).getTime()) / 1000;
                  if (diff < 60) return lang === "fr" ? "à l'instant" : "just now";
                  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
                  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
                  return `${Math.floor(diff / 86400)} j`;
                })()
              : null;
            return (
              <div
                key={svc.service}
                className={`rounded-lg border ${stateColors.ring} px-3 py-3 transition-colors`}
                data-testid={`health-badge-${svc.service}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-lg" aria-hidden>{meta.icon}</span>
                    <span className="text-sm font-medium text-zinc-800 truncate">{svc.name || svc.service}</span>
                  </div>
                  <span className={`w-2.5 h-2.5 rounded-full ${stateColors.dot} flex-shrink-0 mt-1.5`} title={stateColors.label} aria-label={stateColors.label} />
                </div>
                <div className={`mt-2 text-[11px] font-semibold ${stateColors.textCls}`} data-testid={`health-status-${svc.service}`}>
                  {stateColors.label}
                </div>
                <div className="mt-1 text-[10px] text-zinc-500 space-y-0.5">
                  {svc.key_masked && <div className="truncate" title={svc.key_masked}>🔑 {svc.key_masked}</div>}
                  {lt?.latency_ms != null && <div>⚡ {lt.latency_ms} ms</div>}
                  {testedAgo && (
                    <div title={lt?.tested_by || ""}>
                      🕒 {lang === "fr" ? "testé " : "tested "}{testedAgo}{lang === "fr" ? "" : " ago"}
                      {String(lt?.tested_by || "").includes("sentinelle") && (
                        <span className="ml-1 text-teal-600">
                          {lang === "fr" ? "· sentinelle" : "· sentinel"}
                        </span>
                      )}
                    </div>
                  )}
                  {state === "failing" && lt?.error_type && (
                    <div className="text-rose-600 font-medium truncate" title={lt.message}>⚠️ {lt.error_type}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Budget mensuel + alertes quota */}
      {/* Vincent #149 (2026-06-12) — Consommation AI : mois en cours + moyennes
          mensuelles sur 3 et 12 mois, directement dans la section Forfait AI. */}
      {consumptionSummary && (
        <div className="mb-6 border border-zinc-200 rounded-xl p-5 bg-white" data-testid="ai-package-consumption-summary">
          <h3 className="text-sm font-semibold text-zinc-800 mb-3">
            📊 {lang === "fr" ? "Consommation de crédits AI (estimation, CAD$)" : "AI credit consumption (estimate, CAD$)"}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3" data-testid="consumption-current-month">
              <p className="text-[11px] text-zinc-500 uppercase font-semibold">{lang === "fr" ? "Mois en cours" : "Current month"}</p>
              <p className="text-xl font-bold text-[#002FA7]">{consumptionSummary.current_month_cad.toFixed(2)} $</p>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="consumption-avg-3m">
              <p className="text-[11px] text-zinc-500 uppercase font-semibold">{lang === "fr" ? "Moyenne / mois (3 derniers mois)" : "Avg / month (last 3 months)"}</p>
              <p className="text-xl font-bold text-zinc-800">{consumptionSummary.avg_monthly_3m_cad.toFixed(2)} $</p>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="consumption-avg-12m">
              <p className="text-[11px] text-zinc-500 uppercase font-semibold">{lang === "fr" ? "Moyenne / mois (12 derniers mois)" : "Avg / month (last 12 months)"}</p>
              <p className="text-xl font-bold text-zinc-800">{consumptionSummary.avg_monthly_12m_cad.toFixed(2)} $</p>
            </div>
          </div>
          <p className="text-[10px] text-zinc-400 mt-2">
            {lang === "fr"
              ? "Estimations basées sur votre activité (transcriptions, générations IA, stockage). Détail complet dans « Voir ma consommation »."
              : "Estimates based on your activity. Full breakdown in “View consumption”."}
          </p>
        </div>
      )}

      {/* B1076 (Eric 2026-09-05 SPEC STRICK PROD) — Destinataires et
          périodicité du rapport forfait AI, propres à cette organisation. */}
      <div className="mb-6">
        <ConsumptionSeriesChart />
        <AIReportSettingsCard />
      </div>

      <div className="mb-6 border border-zinc-200 rounded-xl p-5 bg-gradient-to-br from-white to-blue-50/30" data-testid="ai-package-budget-card">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💰</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-zinc-900">
              {lang === "fr" ? "Budget mensuel & alertes" : "Monthly budget & alerts"}
            </h3>
            <p className="text-xs text-zinc-600 mt-1 max-w-xl">
              {lang === "fr"
                ? "Définissez un budget mensuel cible (CAD) pour recevoir des alertes par email aux paliers 75%, 90% et 100%. Évitez toute facture surprise OpenAI / AssemblyAI / OVH / Resend en fin de mois."
                : "Set a monthly budget target (CAD) to receive email alerts at 75%, 90% and 100%. Avoid any surprise OpenAI / AssemblyAI / OVH / Resend bill."}
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">
                  {lang === "fr" ? "Budget cible (CAD$)" : "Target budget (CAD$)"}
                </label>
                <input
                  type="number"
                  min="0"
                  step="10"
                  value={budget.monthly_cad}
                  onChange={(e) => setBudget((p) => ({ ...p, monthly_cad: e.target.value }))}
                  placeholder={lang === "fr" ? "Ex: 150 (laisser vide = pas d'alerte)" : "Ex: 150 (empty = no alert)"}
                  className="w-56 px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                  data-testid="ai-package-budget-input"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={budget.alerts_enabled}
                  onChange={(e) => setBudget((p) => ({ ...p, alerts_enabled: e.target.checked }))}
                  className="w-4 h-4"
                  data-testid="ai-package-budget-alerts-toggle"
                />
                {lang === "fr" ? "Activer les alertes email (75% / 90% / 100%)" : "Enable email alerts (75% / 90% / 100%)"}
              </label>
              <button
                onClick={saveBudget}
                disabled={savingBudget}
                className="px-4 py-2 text-xs rounded-lg bg-[#002FA7] hover:bg-[#001F7A] text-white font-semibold disabled:opacity-50"
                data-testid="ai-package-budget-save-btn"
              >
                {savingBudget ? (lang === "fr" ? "Sauvegarde..." : "Saving...") : (lang === "fr" ? "Sauvegarder" : "Save")}
              </button>
            </div>
            {budget.configured && (
              <p className="text-[10px] text-teal-700 mt-2" data-testid="ai-package-budget-active">
                ✓ {lang === "fr" ? "Alertes actives" : "Alerts active"} ({budget.monthly_cad} CAD$/{lang === "fr" ? "mois" : "month"})
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {services.map((svc) => {
          const meta = SERVICE_LOGOS[svc.service] || { icon: "🔌", color: "zinc" };
          const isEditing = editing === svc.service;
          return (
            <div
              key={svc.service}
              className={`border rounded-xl p-5 transition-all ${svc.configured ? `border-${meta.color}-200 bg-${meta.color}-50/40` : "border-zinc-200 bg-white"}`}
              data-testid={`ai-package-service-${svc.service}`}
            >
              <div className="flex items-start gap-3">
                <div className="text-3xl">{meta.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-zinc-900">{libelleService(svc, lang)}</h3>
                    {svc.configured && svc.enabled === false ? (
                      <span
                        className="px-2 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-800 border border-amber-300"
                        data-testid={`ai-package-key-disabled-badge-${svc.service}`}
                      >
                        ⏸ {lang === "fr" ? "Clé désactivée" : "Key disabled"}
                      </span>
                    ) : svc.configured ? (
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-teal-100 text-teal-700 border border-teal-300">
                        ✓ {lang === "fr" ? "Configuré" : "Configured"}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-zinc-100 text-zinc-600 border border-zinc-300">
                        {lang === "fr" ? "Non configuré" : "Not configured"}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-600 mt-1">{descriptionService(svc, lang)}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                    <a href={svc.signup_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`ai-package-signup-${svc.service}`}>
                      🔗 {lang === "fr" ? "Créer un compte" : "Sign up"}
                    </a>
                    <span className="text-zinc-500">💰 {lang === "fr" ? "Coût estimé" : "Estimated cost"} : <strong>{coutService(svc, lang)}</strong></span>
                  </div>
                  {/* Eric/JS 2026-06-02 — CTA proéminent vers le guide pas-à-pas
                      quand le service n'est PAS encore configuré. JS s'est perdu
                      dans OVH sans trouver le guide intégré — on le pousse en
                      avant. Deep-link via ?service= pour ouvrir directement la
                      section concernée + scroll automatique. */}
                  {!svc.configured && !isEditing && (
                    <Link
                      to={`/dashboard/settings/ai-package/guide?service=${svc.service}`}
                      className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 text-sm font-semibold transition-colors"
                      data-testid={`ai-package-help-${svc.service}`}
                      title={lang === "fr" ? `Ouvre le guide étape-par-étape pour ${libelleService(svc, lang)}` : `Open the step-by-step guide for ${libelleService(svc, lang)}`}
                    >
                      📖 {lang === "fr" ? "Voir la marche à suivre" : "View setup guide"}
                      {/* Tâche 1431 — « 3 étapes » restait en français : le
                          nombre change selon le service, le MOT doit suivre la
                          langue. */}
                      <span className="text-[10px] uppercase font-bold bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded">
                        {(svc.service === "ovh_storage" ? 5 : svc.service === "openai" ? 4 : svc.service === "embeddings" ? 4 : 3)}
                        {lang === "fr" ? " étapes" : " steps"}
                      </span>
                    </Link>
                  )}
                  {svc.configured && (
                    <div className="mt-3 flex items-start justify-between gap-3 px-3 py-2 rounded-lg border border-zinc-200 bg-white">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-zinc-800">
                          {svc.enabled === false
                            ? (lang === "fr" ? "Clé en pause" : "Key paused")
                            : (lang === "fr" ? "Clé active" : "Key active")}
                        </div>
                        <p className="text-[11px] text-zinc-500 max-w-md">
                          {svc.enabled === false
                            ? (lang === "fr"
                                ? `En pause, cette clé n'est plus utilisée : ${libelleService(svc, lang)} est indisponible pour cette organisation (sauf si les clés Knoria sont forcées).`
                                : "Paused: this key is never used.")
                            : (lang === "fr"
                                ? "Mettre en pause suspend ce service sans supprimer la clé — inutile de la ressaisir ensuite."
                                : "Pausing suspends this service without deleting the key.")}
                        </p>
                        {svc.enabled === false && svc.disabled_by && (
                          <p
                            className="text-[11px] text-amber-700 mt-0.5"
                            data-testid={`ai-package-key-disabled-by-${svc.service}`}
                          >
                            {lang === "fr" ? "Désactivée par" : "Disabled by"} {svc.disabled_by}
                            {svc.disabled_at ? ` — ${new Date(svc.disabled_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA")}` : ""}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleKeyEnabled(svc.service, svc.enabled === false)}
                        disabled={togglingService === svc.service}
                        role="switch"
                        aria-checked={svc.enabled !== false}
                        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                          svc.enabled === false ? "bg-zinc-300" : "bg-[#002FA7]"
                        }`}
                        data-testid={`ai-package-key-toggle-${svc.service}`}
                        title={svc.enabled === false
                          ? (lang === "fr" ? "Réactiver cette clé" : "Re-enable this key")
                          : (lang === "fr" ? "Mettre cette clé en pause" : "Pause this key")}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            svc.enabled === false ? "translate-x-0.5" : "translate-x-5"
                          }`}
                        />
                      </button>
                    </div>
                  )}
                  {svc.configured && !isEditing && (
                    <div className="mt-3 px-3 py-2 rounded bg-white border border-zinc-200 text-xs font-mono text-zinc-600 flex items-center justify-between gap-2">
                      <span data-testid={`ai-package-key-masked-${svc.service}`}>{svc.key_masked}</span>
                      <span className="text-[10px] text-zinc-400">
                        {lang === "fr" ? "Mis à jour" : "Updated"} : {svc.updated_at ? new Date(svc.updated_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA") : "—"}
                      </span>
                    </div>
                  )}
                  {/* Vincent #148 (2026-06-12) — Test de connexion RÉEL : fait un
                      vrai appel API avec la clé effectivement utilisée par le
                      système (BYOK ou clé Knoria forcée) et affiche le diagnostic
                      exact (crédit épuisé / clé invalide / rate limit) avec la
                      marche à suivre. */}
                  {svc.configured && !isEditing && svc.service !== "ovh_storage" && (
                    <div className="mt-2">
                      <button
                        onClick={() => runConnectionTest(svc.service)}
                        disabled={testingService === svc.service}
                        className="px-3 py-1.5 rounded-lg border border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
                        data-testid={`ai-package-test-${svc.service}`}
                      >
                        {testingService === svc.service
                          ? (lang === "fr" ? "⏳ Test en cours…" : "⏳ Testing…")
                          : (lang === "fr" ? "🧪 Tester la connexion" : "🧪 Test connection")}
                      </button>
                      {testResults[svc.service] && (
                        <div
                          className={`mt-2 px-3 py-2.5 rounded-lg border text-xs space-y-1 ${
                            testResults[svc.service].ok
                              ? "bg-teal-50 border-teal-200 text-teal-800"
                              : "bg-rose-50 border-rose-200 text-rose-800"
                          }`}
                          data-testid={`ai-package-test-result-${svc.service}`}
                        >
                          <p className="font-semibold">
                            {testResults[svc.service].ok ? "✅ " : "❌ "}
                            {testResults[svc.service].message}
                          </p>
                          {testResults[svc.service].source && (
                            <p className="text-[10px] opacity-80">
                              {lang === "fr" ? "Clé utilisée par le système : " : "Key used by system: "}
                              <strong>
                                {testResults[svc.service].source === "byok"
                                  ? (lang === "fr" ? "clé BYOK de votre organisation" : "your org's BYOK key")
                                  : testResults[svc.service].source === "knoria_forced"
                                    ? (lang === "fr" ? "clé Knoria (toggle « Clés AI Knoria » actif)" : "Knoria key (forced)")
                                    : (lang === "fr" ? "clé plateforme Knoria" : "Knoria platform key")}
                              </strong>
                              {testResults[svc.service].key_last4 ? ` (••••${testResults[svc.service].key_last4})` : ""}
                            </p>
                          )}
                          {testResults[svc.service].suggestion && (
                            <p className="text-[11px] bg-white/60 rounded px-2 py-1.5 mt-1">
                              💡 {testResults[svc.service].suggestion}
                            </p>
                          )}
                          {!testResults[svc.service].ok && svc.service === "openai" && (
                            <a
                              href="https://platform.openai.com/settings/organization/billing/overview"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block text-[11px] font-semibold underline"
                              data-testid="openai-billing-link"
                            >
                              → {lang === "fr" ? "Ouvrir la facturation OpenAI (ajouter des crédits)" : "Open OpenAI billing"}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {svc.configured && svc.extra?.bucket_name && !isEditing && (
                    <div className="mt-2 text-xs text-zinc-600">
                      📦 {lang === "fr" ? "Bucket" : "Bucket"} : <strong>{svc.extra.bucket_name}</strong> ({svc.extra.region})
                    </div>
                  )}

                  {isEditing && (
                    <div className="mt-4 p-4 rounded-lg bg-white border border-zinc-200 space-y-3" data-testid={`ai-package-editor-${svc.service}`}>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-700 mb-1">
                          {svc.service === "ovh_storage" ? "OVH Access Key ID" : (lang === "fr" ? "Clé API" : "API key")}
                        </label>
                        <input
                          type="password"
                          value={formData.key}
                          onChange={(e) => setFormData(p => ({ ...p, key: e.target.value }))}
                          className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                          placeholder={svc.service === "openai" ? "sk-..." : (svc.service === "ovh_storage" ? "OVH Access Key (généré dans le portail)" : (svc.service === "resend" ? "re_..." : (svc.service === "embeddings" ? "sk-... (OpenAI) ou hf_... (Hugging Face)" : "...")))}
                          data-testid={`ai-package-input-key-${svc.service}`}
                        />
                      </div>
                      {svc.service === "ovh_storage" && (
                        <>
                          <div>
                            <label className="block text-xs font-semibold text-zinc-700 mb-1">OVH Secret Access Key</label>
                            <input
                              type="password"
                              value={formData.secret_key}
                              onChange={(e) => setFormData(p => ({ ...p, secret_key: e.target.value }))}
                              className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                              data-testid="ai-package-input-secret-key"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-zinc-700 mb-1">{tr("Bucket OVH Object Storage (région BHS — Beauharnois Québec)", "OVH Object Storage bucket (BHS region — Beauharnois, Quebec)")}</label>
                            <input
                              type="text"
                              value={formData.bucket_name}
                              onChange={(e) => setFormData(p => ({ ...p, bucket_name: e.target.value }))}
                              className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                              placeholder="mon-bucket-knoria"
                              data-testid="ai-package-input-bucket"
                            />
                            <p className="text-[10px] text-zinc-500 mt-1">
                              ⚠️ {lang === "fr"
                                ? "Le bucket DOIT être créé en région BHS (Beauharnois, Québec) pour la conformité Loi 25. Les régions européennes (GRA, SBG, RBX) sont interdites."
                                : "Bucket MUST be in BHS (Beauharnois, Québec) for Loi 25 compliance. European regions (GRA, SBG, RBX) forbidden."}
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-semibold text-zinc-700 mb-1">{tr("Région", "Region")}</label>
                              <select
                                value={formData.region}
                                onChange={(e) => setFormData(p => ({ ...p, region: e.target.value }))}
                                className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                                data-testid="ai-package-input-region"
                              >
                                <option value="bhs">{tr("BHS · Beauharnois (Québec)", "BHS · Beauharnois, Quebec")}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-zinc-700 mb-1">Endpoint S3</label>
                              <input
                                type="text"
                                value={formData.endpoint}
                                onChange={(e) => setFormData(p => ({ ...p, endpoint: e.target.value }))}
                                className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                                placeholder="https://s3.bhs.io.cloud.ovh.net"
                                data-testid="ai-package-input-endpoint"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {svc.service === "resend" && (
                        <>
                          <div>
                            <label className="block text-xs font-semibold text-zinc-700 mb-1">
                              {lang === "fr" ? "Adresse expéditrice (sender_email)" : "Sender email"}
                            </label>
                            <input
                              type="email"
                              value={formData.sender_email || ""}
                              onChange={(e) => setFormData(p => ({ ...p, sender_email: e.target.value }))}
                              className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                              placeholder="noreply@votre-domaine.com"
                              data-testid="ai-package-input-sender-email"
                            />
                            <p className="text-[10px] text-zinc-500 mt-1">
                              ⚠️ {lang === "fr"
                                ? "Le domaine doit être VÉRIFIÉ sur Resend (Dashboard → Domains → Add Domain → SPF/DKIM). Sinon les emails seront rejetés."
                                : "Domain must be VERIFIED on Resend (Dashboard → Domains → Add Domain → SPF/DKIM). Otherwise emails are rejected."}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-zinc-700 mb-1">
                              {lang === "fr" ? "Reply-To (optionnel)" : "Reply-To (optional)"}
                            </label>
                            <input
                              type="email"
                              value={formData.reply_to || ""}
                              onChange={(e) => setFormData(p => ({ ...p, reply_to: e.target.value }))}
                              className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                              placeholder="support@votre-domaine.com"
                              data-testid="ai-package-input-reply-to"
                            />
                          </div>
                        </>
                      )}
                      <div className="flex items-center gap-2 justify-end pt-2 border-t border-zinc-100">
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1.5 text-xs rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700"
                          data-testid={`ai-package-cancel-${svc.service}`}
                        >
                          {lang === "fr" ? "Annuler" : "Cancel"}
                        </button>
                        <button
                          onClick={() => saveKey(svc.service)}
                          disabled={saving}
                          className="px-3 py-1.5 text-xs rounded bg-[#002FA7] hover:bg-[#001F7A] text-white font-medium disabled:opacity-50"
                          data-testid={`ai-package-save-${svc.service}`}
                        >
                          {saving ? (lang === "fr" ? "Validation..." : "Validating...") : (lang === "fr" ? "Tester et sauvegarder" : "Test and save")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {!isEditing && (
                <div className="mt-3 flex items-center gap-2 justify-end">
                  <button
                    onClick={() => openEditor(svc.service)}
                    className="px-3 py-1.5 text-xs rounded border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 font-medium"
                    data-testid={`ai-package-edit-${svc.service}`}
                  >
                    ✏️ {svc.configured ? (lang === "fr" ? "Changer la clé" : "Change key") : (lang === "fr" ? "Configurer" : "Configure")}
                  </button>
                  {svc.configured && (
                    <button
                      onClick={() => deleteKey(svc.service)}
                      className="px-3 py-1.5 text-xs rounded border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 font-medium"
                      data-testid={`ai-package-delete-${svc.service}`}
                    >
                      🗑 {lang === "fr" ? "Supprimer" : "Remove"}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Eric BYOK-AUDIT (2026-02-06) — Modale de confirmation typée pour suppression de clé.
          Exige la saisie littérale du mot "SUPPRIMER" pour activer le bouton de validation.
          Empêche les clics accidentels (cause probable de la disparition des clés Knoria). */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)} data-testid="byok-delete-confirm-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="byok-delete-confirm-modal">
            <div className="px-5 py-4 border-b border-rose-200 bg-rose-50 rounded-t-xl">
              <h2 className="font-bold text-lg text-rose-800 flex items-center gap-2">
                ⚠️ {lang === "fr" ? "Suppression de clé BYOK" : "Delete BYOK Key"}
              </h2>
              <p className="text-xs text-rose-700 mt-1">
                {lang === "fr"
                  ? "Cette action est tracée dans le journal d'audit (Loi 25). Elle est irréversible côté UI — utilise la restauration fondateur si besoin."
                  : "This action is logged to the audit trail. It is irreversible from the UI — use founder restore if needed."}
              </p>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                <div className="text-xs text-zinc-500">{lang === "fr" ? "Service" : "Service"}</div>
                <div className="text-sm font-semibold text-zinc-900">{deleteConfirm.name}</div>
                <div className="text-xs text-zinc-500 mt-2">{lang === "fr" ? "Clé actuelle" : "Current key"}</div>
                <div className="text-sm font-mono text-zinc-700">{deleteConfirm.masked}</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1">
                  {lang === "fr"
                    ? <>Pour confirmer, tape <span className="font-mono text-rose-700">SUPPRIMER</span> ci-dessous :</>
                    : <>To confirm, type <span className="font-mono text-rose-700">SUPPRIMER</span> below:</>}
                </label>
                <input
                  type="text"
                  autoFocus
                  value={deleteConfirm.typed}
                  onChange={(e) => setDeleteConfirm({ ...deleteConfirm, typed: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-rose-300 rounded-lg font-mono uppercase text-sm focus:outline-none focus:border-rose-500"
                  placeholder="SUPPRIMER"
                  data-testid="byok-delete-confirm-input"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 rounded-b-xl flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm rounded-lg border border-zinc-300 hover:bg-zinc-100 font-medium"
                data-testid="byok-delete-cancel"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button
                onClick={confirmDeleteKey}
                disabled={deleteConfirm.typed.trim().toUpperCase() !== "SUPPRIMER"}
                className="px-4 py-2 text-sm rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="byok-delete-confirm-submit"
              >
                🗑 {lang === "fr" ? "Confirmer la suppression" : "Confirm delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
